import useMarkdown from '@form-engine/core-application/hooks/use-markdown';
import { compile } from '@form-engine/core-util/template';
import React from 'react';
import { FormCheck, FormControl, FormGroup } from 'react-bootstrap';
import { DatasourceResponseOptions } from '@form-engine/core-application/types/form';
import { useFormElementDatasourceData } from '@form-engine/core-application/hooks/use-datasource';
import { useFormContext } from '@form-engine/core-application/hooks/use-form';
import {
    useFormElementSession,
    useFormSessionLocals,
} from '@form-engine/core-application/hooks/use-formsession';
import { useMemo } from 'react';
import { useCallback } from 'react';
import { useFormEngineElement } from '@form-engine/core-application/hooks/use-element';
import { useFormEngineElementDisabled } from '@form-engine/core-application/hooks/use-element-disabled';
import { useFormEngineElementRequired } from '@form-engine/core-application/hooks/use-element-required';
import { apiName } from './checkbox-element';
import uuid from '@form-engine/core-util/uuid';

export interface FormCheckListRendererProps {
    inline?: boolean;
    required?: boolean;
    disabled?: boolean;
    isTouched?: boolean;
    showError?: boolean;
    showValidFeedback?: boolean;
    label?: string;
    error?: string;
    locals: Record<string, unknown>;
    options: DatasourceResponseOptions | string[][];
    currentValue: string[];
    name: string;
    onChange: (e: React.ChangeEvent) => void;
}

export function FormCheckListRenderer({
    inline,
    disabled,
    isTouched,
    showError,
    showValidFeedback,
    label: listLabel,
    error,
    locals,
    options,
    currentValue,
    name,
    onChange,
}: FormCheckListRendererProps) {
    const compileMarkdown = useMarkdown();

    const id = useMemo(() => uuid(), []);

    const controls = options.map((option, key) => {
        let label, value;
        if (Array.isArray(option)) {
            [value, label] = option;
        } else {
            label = value = option;
        }

        label = (
            <span
                dangerouslySetInnerHTML={{ __html: compileMarkdown(label) }}
            />
        );

        value = compile(String(value ?? ''), locals, '');

        const selected = currentValue.includes(value);

        return (
            <FormCheck
                key={key}
                id={`cb-${id}-${key}`}
                inline={Boolean(inline)}
                name={name}
                type="checkbox"
                label={label}
                checked={selected}
                disabled={disabled}
                isValid={showValidFeedback && isTouched && !error}
                isInvalid={Boolean(isTouched && error)}
                onChange={onChange}
                value={value}
            />
        );
    });

    const errorElement = isTouched && showError && error && (
        <FormControl.Feedback type="invalid">{error}</FormControl.Feedback>
    );

    return (
        <FormGroup className="fe-checkbox">
            <>
                {listLabel && <p className="form-label">{listLabel}</p>}
                {controls}
                {errorElement}
            </>
        </FormGroup>
    );
}

export default function FormCheckList() {
    const {
        state: { showValidFeedback = true },
    } = useFormContext();
    const { state: element } = useFormEngineElement();
    if (!element || element.type !== apiName)
        throw new Error('tried to render checkbox with invalid definition');

    const [session, setSession] = useFormElementSession();

    const currentValue = useMemo(() => {
        return Array.isArray(session?.value)
            ? session.value
            : String(session.value || '')
                  .trim()
                  .split(',');
    }, [session.value]);

    const handleChange = useCallback(
        (e: React.ChangeEvent) => {
            const target = e.target as HTMLInputElement;

            const getStringValue = (source: string[]) =>
                source.filter((v) => Boolean(v)).join(',');

            if (target.checked && !currentValue.includes(target.value)) {
                setSession({
                    ...session,
                    isTouched: true,
                    value: getStringValue([...currentValue, target.value]),
                });
            } else if (!target.checked && currentValue.includes(target.value)) {
                const copy = [...currentValue];
                copy.splice(copy.indexOf(target.value), 1);
                setSession({
                    ...session,
                    isTouched: true,
                    value: getStringValue(copy),
                });
            }
        },
        [setSession, session, currentValue]
    );

    const props: FormCheckListRendererProps = {
        inline: element.inline,
        required: useFormEngineElementRequired(),
        disabled: useFormEngineElementDisabled(),
        isTouched: session.isTouched,
        showError: element.showError,
        showValidFeedback,
        label: element.label,
        error: session.error || undefined,
        locals: useFormSessionLocals()?.state,
        options: useFormElementDatasourceData()?.options,
        currentValue,
        name: String(element.name),
        onChange: handleChange,
    };

    return <FormCheckListRenderer {...props} />;
}
